/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The Demand Sources list's filter lives in the URL** (`LP12`, `docs/LISTS_AND_PANELS_DESIGN.md`).
 *
 * It lived in a zustand store, so Back from a source's page returned to an unfiltered list (§3.5
 * measured the loss) and a filtered list could not be linked. The query string is the one home now:
 * read here, written here, and nothing else keeps a copy — a viewer convenience needs no storage of
 * its own (`#1380`'s ruling on remembered state).
 */

import {
  BASE_LIST_FILTER,
  isListFiltered,
  listFilterFromSearchParams,
  oneOfParam,
  searchParamsWithListFilter,
  searchParamsWithoutListFilter,
  type ListFilterDeclaration,
} from './artifact-list-filter';
import type { DemandSource, DemandSourceListFilter, DemandSourceMethod, ValidationFilter } from '../types';
import { isSystemManaged } from '@artifact-stewardship';

export { matchesOwnerFilter, ownersOf } from './artifact-list-filter';

/**
 * **Whether a source matches what was typed into a source picker** (`#1604`). The Demand Sources list
 * searches on the server; a picker filters the sources it was handed, so it matches here. Every word
 * typed must match something the source shows: its name or id, its protocol, its owner (or *system*
 * for a system-managed one), *required* for a source whose terms require it, *disabled* for one
 * switched off. So `ortb required` narrows to the required ORTB sources.
 */
export function matchesDemandSourceSearch(
  source: Pick<DemandSource, 'id' | 'name' | 'method' | 'enabled' | 'ownerId' | 'ownerName' | 'managedBy' | 'terms'>,
  query: string,
): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const system = isSystemManaged({ managedBy: source.managedBy, ownerId: source.ownerId });
  const haystack = [
    source.name ?? '',
    String(source.id ?? ''),
    source.method ?? '',
    source.method === 'ORTB' ? 'openrtb' : '',
    system ? 'system' : (source.ownerName ?? source.ownerId ?? ''),
    source.terms?.required === true ? 'required' : '',
    source.enabled === false ? 'disabled' : '',
  ]
    .join(' ')
    .toLowerCase();
  return words.every((w) => haystack.includes(w));
}

export const DEFAULT_DEMAND_SOURCE_FILTER: Readonly<Required<DemandSourceListFilter>> = {
  search: '',
  method: '',
  validation: '',
  enabled: null,
  owner: '',
  sortBy: 'name',
  sortDir: 'asc',
  page: 1,
  pageSize: 20,
} as const;

const METHODS: readonly DemandSourceMethod[] = ['VAST', 'ORTB', 'API'];

/** What the URL carries: the shared parameters and the two this type adds. */
type UrlFilter = Required<Omit<DemandSourceListFilter, 'sortBy' | 'pageSize'>>;

/** In the order the URL has always carried them, so an existing link reads back the same. */
const DECLARATION: ListFilterDeclaration<UrlFilter> = {
  search: BASE_LIST_FILTER.search,
  method: oneOfParam('type', METHODS),
  validation: {
    key: 'readiness',
    empty: '',
    read: (raw) => (raw ?? '') as ValidationFilter,
    write: (value) => value || null,
    narrows: true,
  },
  enabled: BASE_LIST_FILTER.enabled,
  owner: BASE_LIST_FILTER.owner,
  sortDir: BASE_LIST_FILTER.sortDir,
  page: BASE_LIST_FILTER.page,
};

export function filterFromSearchParams(params: URLSearchParams): Required<DemandSourceListFilter> {
  return { ...DEFAULT_DEMAND_SOURCE_FILTER, ...listFilterFromSearchParams(DECLARATION, params) };
}

/**
 * The same URL with this change applied. A change to anything but the page returns to page one, as the
 * store did. Every other parameter — `fromTemplate`, `newTemplate` — is left as it was.
 */
export function searchParamsWithFilter(
  params: URLSearchParams,
  patch: Partial<DemandSourceListFilter>,
): URLSearchParams {
  const { sortBy: _sortBy, pageSize: _pageSize, ...urlPatch } = patch;
  return searchParamsWithListFilter(DECLARATION, params, urlPatch as Partial<UrlFilter>);
}

/** Whether anything narrows the list — what separates *nothing matches* from *nothing exists*. */
export function isFiltered(filter: DemandSourceListFilter): boolean {
  return isListFiltered(DECLARATION, { ...DEFAULT_DEMAND_SOURCE_FILTER, ...filter });
}

/** The URL with every filter cleared, keeping anything that is not a filter. */
export function searchParamsWithoutFilter(params: URLSearchParams): URLSearchParams {
  return searchParamsWithoutListFilter(DECLARATION, params);
}
